import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { FiPlus, FiX, FiCopy, FiEdit2 } from 'react-icons/fi';
import Loader from '../../../components/Loader';
import Verificacion from '../../../components/Verificacion';
import BASE_URL from '../../../config/config';
import { cabecerasAuth, claves } from '../../../queries/cliente';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PermisoDenegado from '../../../components/PermisoDenegado';
import './css/admin_style.css';
import Paginacion from '../../../components/ui/Paginacion';

const AjustesDelSistema = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const token = localStorage.getItem('AuthToken');
  const tokenInfo = token ? jwtDecode(token) : {};

  const [minimoTiempoCumplido, setMinimoTiempoCumplido] = useState(false);

  // Filtros y paginación
  const [busqueda, setBusqueda] = useState('');
  const [orden, setOrden] = useState('asc'); // 'asc' | 'desc'
  const [paginaActual, setPaginaActual] = useState(1);
  const [porPagina, setPorPagina] = useState(12);

  // Crear (panel plegable)
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoValor, setNuevoValor] = useState('');
  const [nuevoTipoValor, setNuevoTipoValor] = useState('texto'); // 'texto' | 'json'
  const [creando, setCreando] = useState(false);

  // Editar
  const [editandoId, setEditandoId] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [editValor, setEditValor] = useState('');
  const [editTipoValor, setEditTipoValor] = useState('texto');
  const [guardando, setGuardando] = useState(false);

  // Loader mínimo 1s
  useEffect(() => {
    const t = setTimeout(() => setMinimoTiempoCumplido(true), 1000);
    return () => clearTimeout(t);
  }, []);

  const consultaAjustes = useQuery({
    queryKey: claves.ajustes(),
    queryFn: async () => {
      const resp = await axios.get(`${BASE_URL}/ajustes`, cabecerasAuth());
      return resp.data?.ajustes || [];
    },
    enabled: tokenInfo?.role === 'admin',
  });

  useEffect(() => {
    if (consultaAjustes.isError) toast.error(t('ajustesSistema.errorCargar'));
  }, [consultaAjustes.isError, t]);

  const ajustes = useMemo(() => consultaAjustes.data || [], [consultaAjustes.data]);
  const cargandoLista = consultaAjustes.isPending;

  const cargarAjustes = () => queryClient.invalidateQueries({ queryKey: claves.ajustes() });

  // Helpers
  const esJSONValido = (txt) => {
    try {
      const parsed = JSON.parse(txt);
      return typeof parsed === 'object';
    } catch {
      return false;
    }
  };

  const formatearJSON = (txt) => {
    try {
      return JSON.stringify(JSON.parse(txt), null, 2);
    } catch {
      return txt;
    }
  };

  const detectarTipoValor = (valor) => (esJSONValido(valor) ? 'json' : 'texto');

  // Derivados
  const ajustesFiltradosOrdenados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();
    let list = ajustes.filter((a) => {
      const n = (a.nombre || '').toLowerCase();
      const v = (a.valor || '').toLowerCase();
      return !term || n.includes(term) || v.includes(term);
    });

    list = list.sort((a, b) => {
      if (orden === 'asc') return a.nombre.localeCompare(b.nombre);
      return b.nombre.localeCompare(a.nombre);
    });

    return list;
  }, [ajustes, busqueda, orden]);

  const indicePrimero = (paginaActual - 1) * porPagina;
  const pagina = ajustesFiltradosOrdenados.slice(indicePrimero, indicePrimero + porPagina);

  const resetCrear = () => {
    setNuevoNombre('');
    setNuevoValor('');
    setNuevoTipoValor('texto');
  };

  const empezarEditar = (item) => {
    setEditandoId(item.id ?? item.nombre);
    setEditNombre(item.nombre);
    const valorComoTexto = typeof item.valor === 'string' ? item.valor : String(item.valor ?? '');
    setEditValor(valorComoTexto);
    setEditTipoValor(detectarTipoValor(valorComoTexto));
  };

  const cancelarEditar = () => {
    setEditandoId(null);
    setEditNombre('');
    setEditValor('');
    setEditTipoValor('texto');
  };

  const copiar = async (texto, msgOk) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(msgOk || t('ajustesSistema.copiadoPortapapeles'));
    } catch {
      toast.error(t('ajustesSistema.errorCopiar'));
    }
  };

  // Acciones
  const crearAjuste = async () => {
    if (!nuevoNombre.trim()) {
      return toast.error(t('ajustesSistema.nombreObligatorio'));
    }
    if (nuevoTipoValor === 'json' && !esJSONValido(nuevoValor)) {
      return toast.error(t('ajustesSistema.valorJsonInvalido'));
    }
    setCreando(true);
    try {
      const payload = {
        nombre: nuevoNombre.trim(),
        valor: nuevoTipoValor === 'json' ? JSON.parse(nuevoValor) : nuevoValor,
      };
      await axios.post(`${BASE_URL}/ajustes`, payload, {
        headers: { Authorization: token },
      });
      toast.success(t('ajustesSistema.ajusteCreado'));
      resetCrear();
      setMostrarCrear(false);
      await cargarAjustes();
      setPaginaActual(1);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(t('ajustesSistema.nombreYaExiste'));
      } else {
        toast.error(t('ajustesSistema.errorCrear'));
      }
    } finally {
      setCreando(false);
    }
  };

  const guardarEdicion = async (item) => {
    if (!editNombre.trim()) {
      return toast.error(t('ajustesSistema.nombreObligatorio'));
    }
    if (editTipoValor === 'json' && !esJSONValido(editValor)) {
      return toast.error(t('ajustesSistema.valorJsonInvalido'));
    }
    setGuardando(true);
    try {
      const body = {
        valor: editTipoValor === 'json' ? JSON.parse(editValor) : editValor,
      };
      if (editNombre !== item.nombre) {
        body.nombre = editNombre.trim();
      }
      await axios.put(`${BASE_URL}/ajustes/${encodeURIComponent(item.nombre)}`, body, {
        headers: { Authorization: token },
      });
      toast.success(t('ajustesSistema.ajusteActualizado'));
      cancelarEditar();
      await cargarAjustes();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error(t('ajustesSistema.nombreNuevoYaExiste'));
      } else if (e?.response?.status === 404) {
        toast.error(t('ajustesSistema.ajusteYaNoExiste'));
      } else {
        toast.error(t('ajustesSistema.errorActualizar'));
      }
    } finally {
      setGuardando(false);
    }
  };

  // Loader fullscreen como en el resto
  const mostrarLoader = !minimoTiempoCumplido || (tokenInfo.role === 'admin' && cargandoLista);

  if (tokenInfo.estado === 'verificar') {
    return <Verificacion />;
  }

  if (tokenInfo.role !== 'admin') {
    return <PermisoDenegado />;
  }

  if (mostrarLoader) {
    return (
      <div className="loader-fullscreen">
        <Loader />
      </div>
    );
  }

  return (
    <main className="site-width">
      <ToastContainer position="bottom-right" autoClose={3000} />
      <div className="dashboard-content gap-ajustes">
        <div className="pagina-cabecera">
          <h2>{t('ajustesSistema.titulo')}</h2>
          <p><Trans i18nKey="ajustesSistema.subtitulo" components={{ b: <b /> }} /></p>
        </div>

        {/* Barra de herramientas */}
        <div className="ajustes-toolbar">
          <input
            type="text"
            placeholder={t('ajustesSistema.buscarPlaceholder')}
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setPaginaActual(1);
            }}
          />
          <div className="ajustes-toolbar-acciones">
            <select value={orden} onChange={(e) => setOrden(e.target.value)}>
              <option value="asc">{t('ajustesSistema.nombreAZ')}</option>
              <option value="desc">{t('ajustesSistema.nombreZA')}</option>
            </select>
            <select
              className="selector-page-size"
              value={porPagina}
              onChange={(e) => { setPorPagina(Number(e.target.value)); setPaginaActual(1); }}
            >
              <option value={12}>{t('ajustesSistema.porPagina', { n: 12 })}</option>
              <option value={24}>{t('ajustesSistema.porPagina', { n: 24 })}</option>
              <option value={50}>{t('ajustesSistema.porPagina', { n: 50 })}</option>
            </select>
            <button
              type="button"
              className={`chip-toggle ${mostrarCrear ? 'activo' : ''}`}
              onClick={() => setMostrarCrear((v) => !v)}
            >
              <FiPlus /> {t('ajustesSistema.nuevoAjuste')}
            </button>
          </div>
        </div>

        {/* Panel plegable: crear ajuste */}
        {mostrarCrear && (
          <div className="ajustes-panel-crear">
            <div className="ajustes-panel-crear-header">
              <h3>{t('ajustesSistema.nuevoAjuste')}</h3>
              <button type="button" className="close-inline" onClick={() => setMostrarCrear(false)} aria-label={t('ajustesSistema.cerrar')}>
                <FiX />
              </button>
            </div>

            <div className="ajustes-crear-grid">
              <div className="campo-ajuste">
                <label htmlFor="nuevo-nombre">{t('ajustesSistema.nombre')}</label>
                <input
                  id="nuevo-nombre"
                  type="text"
                  placeholder={t('ajustesSistema.placeholderNombre')}
                  value={nuevoNombre}
                  onChange={(e) => setNuevoNombre(e.target.value)}
                />
              </div>
              <div className="campo-ajuste">
                <label>{t('ajustesSistema.tipoValor')}</label>
                <div className="selector-tipo-valor">
                  <label>
                    <input
                      type="radio"
                      name="tipo-crear"
                      checked={nuevoTipoValor === 'texto'}
                      onChange={() => setNuevoTipoValor('texto')}
                    />
                    <span>{t('ajustesSistema.texto')}</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="tipo-crear"
                      checked={nuevoTipoValor === 'json'}
                      onChange={() => {
                        setNuevoTipoValor('json');
                        if (nuevoValor.trim() && esJSONValido(nuevoValor)) {
                          setNuevoValor(formatearJSON(nuevoValor));
                        }
                      }}
                    />
                    <span>{t('ajustesSistema.json')}</span>
                  </label>
                </div>
              </div>
              <div className="campo-ajuste full">
                <label htmlFor="nuevo-valor">{t('ajustesSistema.valor')}</label>
                {nuevoTipoValor === 'json' ? (
                  <textarea
                    id="nuevo-valor"
                    rows={6}
                    placeholder='{"inicio":"08:00","fin":"14:00"}'
                    value={nuevoValor}
                    onChange={(e) => setNuevoValor(e.target.value)}
                  />
                ) : (
                  <input
                    id="nuevo-valor"
                    type="text"
                    placeholder={t('ajustesSistema.textoLibre')}
                    value={nuevoValor}
                    onChange={(e) => setNuevoValor(e.target.value)}
                  />
                )}
                {nuevoTipoValor === 'json' && (
                  <div className="acciones-json">
                    <button
                      type="button"
                      onClick={() => setNuevoValor(formatearJSON(nuevoValor))}
                      disabled={!nuevoValor.trim() || !esJSONValido(nuevoValor)}
                    >
                      {t('ajustesSistema.formatearJson')}
                    </button>
                    <span className={`ajustes-json-badge ${!nuevoValor.trim() ? '' : esJSONValido(nuevoValor) ? 'ok' : 'error'}`}>
                      {!nuevoValor.trim() ? '—' : esJSONValido(nuevoValor) ? t('ajustesSistema.jsonValido') : t('ajustesSistema.jsonInvalido')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="ajustes-crear-actions">
              <button type="button" className="ajustes-btn-secundario" onClick={resetCrear} disabled={creando}>{t('ajustesSistema.limpiar')}</button>
              <button type="button" className="ajustes-btn-primario" onClick={crearAjuste} disabled={creando}>
                {creando ? t('ajustesSistema.creando') : t('ajustesSistema.crearAjuste')}
              </button>
            </div>
          </div>
        )}

        {/* Tabla de ajustes */}
        <div className="ajustes-panel">
          <div className="ajustes-list-header">
            <span>{t('ajustesSistema.nombre')}</span>
            <span>{t('ajustesSistema.valor')}</span>
            <span>{t('ajustesSistema.acciones')}</span>
          </div>

          <div className="ajustes-rows">
            {pagina.length === 0 ? (
              <div className="ajustes-vacio">{t('ajustesSistema.sinCoincidencias')}</div>
            ) : (
              pagina.map((item) => {
                const key = item.id ?? item.nombre;
                const valorTexto = typeof item.valor === 'string' ? item.valor : String(item.valor ?? '');
                const valorEsJSON = esJSONValido(valorTexto);
                const valorPrettified = valorEsJSON ? formatearJSON(valorTexto) : valorTexto;
                const enEdicion = editandoId === (item.id ?? item.nombre);

                return (
                  <div className={`ajustes-row ${enEdicion ? 'ajustes-row--editando' : ''}`} key={key}>
                    {enEdicion ? (
                      <div className="ajustes-form-edicion">
                        <div className="campo-ajuste">
                          <label>{t('ajustesSistema.nombre')}</label>
                          <input
                            type="text"
                            value={editNombre}
                            onChange={(e) => setEditNombre(e.target.value)}
                          />
                        </div>

                        <div className="campo-ajuste">
                          <label>{t('ajustesSistema.tipoValor')}</label>
                          <div className="selector-tipo-valor">
                            <label>
                              <input
                                type="radio"
                                name={`tipo-${key}`}
                                checked={editTipoValor === 'texto'}
                                onChange={() => setEditTipoValor('texto')}
                              />
                              <span>{t('ajustesSistema.texto')}</span>
                            </label>
                            <label>
                              <input
                                type="radio"
                                name={`tipo-${key}`}
                                checked={editTipoValor === 'json'}
                                onChange={() => {
                                  setEditTipoValor('json');
                                  if (esJSONValido(editValor)) {
                                    setEditValor(formatearJSON(editValor));
                                  }
                                }}
                              />
                              <span>{t('ajustesSistema.json')}</span>
                            </label>
                          </div>
                        </div>

                        <div className="campo-ajuste full">
                          <label>{t('ajustesSistema.valor')}</label>
                          {editTipoValor === 'json' ? (
                            <textarea
                              rows={7}
                              value={editValor}
                              onChange={(e) => setEditValor(e.target.value)}
                            />
                          ) : (
                            <input
                              type="text"
                              value={editValor}
                              onChange={(e) => setEditValor(e.target.value)}
                            />
                          )}
                          {editTipoValor === 'json' && (
                            <div className="acciones-json">
                              <button
                                type="button"
                                onClick={() => setEditValor(formatearJSON(editValor))}
                                disabled={!editValor.trim() || !esJSONValido(editValor)}
                              >
                                {t('ajustesSistema.formatearJson')}
                              </button>
                              <span className={`ajustes-json-badge ${!editValor.trim() ? '' : esJSONValido(editValor) ? 'ok' : 'error'}`}>
                                {!editValor.trim() ? '—' : esJSONValido(editValor) ? t('ajustesSistema.jsonValido') : t('ajustesSistema.jsonInvalido')}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="ajustes-crear-actions">
                          <button type="button" className="ajustes-btn-secundario" onClick={cancelarEditar} disabled={guardando}>
                            {t('ajustesSistema.cancelar')}
                          </button>
                          <button type="button" className="ajustes-btn-primario" onClick={() => guardarEdicion(item)} disabled={guardando}>
                            {guardando ? t('ajustesSistema.guardando') : t('ajustesSistema.guardar')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="ajustes-col col-nombre">
                          <code className="ajustes-badge-key">{item.nombre}</code>
                        </div>

                        <div className="ajustes-col col-valor">
                          <span className={`ajustes-tipo-badge ${valorEsJSON ? 'json' : 'texto'}`}>
                            {valorEsJSON ? t('ajustesSistema.json') : t('ajustesSistema.texto')}
                          </span>
                          {valorEsJSON ? (
                            <pre className="ajustes-valor-json">{valorPrettified}</pre>
                          ) : (
                            <p className="ajustes-valor-texto">{valorPrettified}</p>
                          )}
                        </div>

                        <div className="ajustes-col col-acciones">
                          <button type="button" className="ajustes-btn-icono" onClick={() => empezarEditar(item)} title={t('ajustesSistema.editar')}>
                            <FiEdit2 />
                          </button>
                          <button type="button" className="ajustes-btn-icono" onClick={() => copiar(valorTexto)} title={t('ajustesSistema.copiarValor')}>
                            <FiCopy />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <Paginacion
          pagina={paginaActual}
          porPagina={porPagina}
          total={ajustesFiltradosOrdenados.length}
          onCambiar={setPaginaActual}
        />
      </div>
    </main>
  );
};

export default AjustesDelSistema;
